<template>
  <div class="sco-page py-3">
    <header class="sco-toolbar">
      <div class="sco-toolbar__title">
        <span class="sco-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Store Sales Care-of Verification</h1>
          <p>Walk-in / POS customers often give an alternate number, so the phone never credits the agent who brought them in. Match by name + address instead to credit the right telesales staff</p>
        </div>
      </div>
      <div class="sco-toolbar__actions">
        <CButton size="sm" class="sco-apply-all-btn" :disabled="isApplyingAll || !summary.confident" @click="askApplyAll">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{{ isApplyingAll ? 'Crediting…' : `Credit All Confident (${summary.confident || 0})` }}</span>
        </CButton>
        <CButton size="sm" class="sco-recompute-btn" :disabled="isRecomputing" @click="recompute">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ 'is-spinning': isRecomputing }" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isRecomputing ? 'Recomputing…' : 'Recompute' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="sco-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="sco-alert">{{ successMessage }}</CAlert>

    <section class="sco-tiles">
      <article class="sco-tile sco-tile--total">
        <div class="sco-tile__label">Pending Orders</div>
        <div class="sco-tile__value">{{ summary.total }}</div>
        <div class="sco-tile__breakdown"><span>Walk-in / POS sales awaiting care-of</span></div>
      </article>
      <article class="sco-tile sco-tile--confident">
        <div class="sco-tile__label">Confident</div>
        <div class="sco-tile__value">{{ summary.confident }}</div>
        <div class="sco-tile__breakdown"><span>Single contact + telesales call — one-click apply</span></div>
      </article>
      <article class="sco-tile sco-tile--review">
        <div class="sco-tile__label">Needs Review</div>
        <div class="sco-tile__value">{{ summary.needs_review }}</div>
        <div class="sco-tile__breakdown"><span>Multiple matches, no call, or weak (name-only)</span></div>
      </article>
      <article class="sco-tile sco-tile--nomatch">
        <div class="sco-tile__label">No Match</div>
        <div class="sco-tile__value">{{ summary.no_match }}</div>
        <div class="sco-tile__breakdown"><span>No OMS contact shares this name</span></div>
      </article>
    </section>

    <section class="sco-section">
      <header class="sco-section__head"><h2>Filters</h2></header>
      <div class="sco-section__body">
        <div class="sco-filters">
          <div class="sco-field sco-field--full">
            <label class="sco-label">Search</label>
            <AppInput no-margin :value="filters.search" @update:value="onSearchInput" placeholder="Search by order number, customer name, or address…" />
          </div>
          <div class="sco-field">
            <label class="sco-label">From Order Date</label>
            <SingleDatePicker :value="filters.from" @input="filters.from = $event" />
          </div>
          <div class="sco-field">
            <label class="sco-label">To Order Date</label>
            <SingleDatePicker :value="filters.to" @input="filters.to = $event" />
          </div>
          <div class="sco-field">
            <label class="sco-label">Organisation</label>
            <AppDropdown v-model="filters.organisation_id" :options="orgOptions" placeholder="Any Organisation" />
          </div>
          <div class="sco-field">
            <label class="sco-label">Status</label>
            <AppDropdown v-model="filters.status" :options="statusOptions" placeholder="Any Status" />
          </div>
          <div class="sco-field sco-field--action">
            <label class="sco-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="sco-apply-btn" :disabled="isLoading" @click="applyFilters">{{ isLoading ? 'Loading…' : 'Apply' }}</button>
          </div>
          <div class="sco-field sco-field--action">
            <label class="sco-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="sco-reset-btn" @click="resetFilters">Reset</button>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Store Sales"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      max-body-height="65vh"
      empty-text="No pending store sales match these filters."
    >
      <template #toolbar>
        <span v-if="pagination.total" class="sco-count">{{ rangeLabel }}</span>
      </template>
      <template #cell-customer="{ row }">
        <div class="sco-customer-cell">
          <span class="sco-customer-name">{{ row.customer }}</span>
          <span class="sco-customer-addr" :title="row.address">{{ row.address || '—' }}</span>
        </div>
      </template>
      <template #cell-matched="{ row }">
        <div v-if="row._real_phone" class="sco-match-cell">
          <span class="sco-match-phone">{{ row._real_phone }}</span>
          <span v-if="row._matched_contact_name" class="sco-match-name">{{ row._matched_contact_name }}</span>
        </div>
        <span v-else class="sco-muted">—</span>
      </template>
      <template #cell-staff="{ row }">
        <span v-if="row._care_of_name" class="sco-staff">{{ row._care_of_name }}</span>
        <span v-else class="sco-muted">—</span>
      </template>
      <template #cell-status="{ row }">
        <span class="sco-pill" :class="statusPillClass(row._status)">{{ statusLabel(row._status) }}</span>
        <div v-if="row._reason" class="sco-reason">{{ row._reason }}</div>
      </template>
      <template #cell-action="{ row }">
        <div class="sco-row-actions">
          <template v-if="row._status === 'confident'">
            <button type="button" class="sco-act-btn" :disabled="applyingIds[row._id]" @click="applyConfident(row)">
              {{ applyingIds[row._id] ? '…' : 'Credit' }}
            </button>
            <button type="button" class="sco-act-btn sco-act-btn--ghost" @click="openReview(row)">Review</button>
          </template>
          <button
            v-else-if="row._status === 'needs_review'"
            type="button"
            class="sco-act-btn sco-act-btn--review"
            @click="openReview(row)"
          >
            Review
          </button>
          <button
            v-else-if="row._status === 'credited'"
            type="button"
            class="sco-act-btn sco-act-btn--danger"
            @click="askUncredit(row)"
          >
            Uncredit
          </button>
          <button
            v-else-if="row._status === 'rejected' || row._status === 'oms_customer'"
            type="button"
            class="sco-act-btn sco-act-btn--ghost"
            @click="askReopen(row)"
          >
            Reopen
          </button>
          <span v-else class="sco-muted">—</span>
        </div>
      </template>
    </BaseTable>

    <div v-if="!isLoading && tableRows.length && pagination.last_page > 1" class="sco-pagination-bar">
      <span class="sco-pagination-info">{{ rangeLabel }}</span>
      <AppPager :current-page="Number(pagination.current_page) || 1" :last-page="Number(pagination.last_page) || 1" @change="goToPage" />
    </div>

    <CModal
      :show.sync="reviewOpen"
      size="lg"
      :centered="true"
      title="Review Store Order"
      class="sco-review-modal"
      :close-on-backdrop="!isApprovingReview"
    >
      <div v-if="reviewRow" class="sco-review-body">
        <div v-if="reviewError" class="sco-review-alert">{{ reviewError }}</div>

        <div class="sco-review-order">
          <div class="sco-review-order__head">
            <span class="sco-review-eyebrow">Store Order</span>
            <h3>{{ reviewRow.customer || '—' }}</h3>
          </div>
          <div class="sco-review-order__grid">
            <div><span class="sco-rk">Order #</span><span class="sco-rv">{{ reviewRow.order_number || '—' }}</span></div>
            <div><span class="sco-rk">Counter Phone</span><span class="sco-rv">{{ reviewRow.phone || '—' }}</span></div>
            <div class="sco-review-order__addr"><span class="sco-rk">Address</span><span class="sco-rv">{{ reviewRow.address || '—' }}</span></div>
            <div v-if="reviewOrderTokens.length" class="sco-review-order__addr">
              <span class="sco-rk">Address tokens</span>
              <span class="sco-rv"><span v-for="t in reviewOrderTokens" :key="t" class="sco-token">{{ t }}</span></span>
            </div>
          </div>
        </div>

        <div class="sco-review-head">
          <h4>Matching OMS Contacts <span v-if="!isLoadingCandidates">({{ reviewCandidates.length }})</span></h4>
          <p>Contacts that share this customer's name and overlap the address. Pick the one this customer is — its telesales call sets the care-of credit. The counter phone is left unchanged.</p>
        </div>

        <div v-if="isLoadingCandidates" class="sco-review-empty">Loading candidates…</div>
        <div v-else-if="!reviewCandidates.length" class="sco-review-empty">No name + address match for this order. Assign an agent manually below if a telesales staffer claims it (the phone won't change).</div>
        <div v-else class="sco-review-cards">
          <label
            v-for="cand in reviewCandidates"
            :key="cand.contact_id"
            class="sco-cand"
            :class="{ 'is-picked': pickedContactId === cand.contact_id }"
          >
            <input type="radio" :value="cand.contact_id" :checked="pickedContactId === cand.contact_id" class="sco-cand__radio" @change="pickCandidate(cand)" />
            <div class="sco-cand__body">
              <div class="sco-cand__head">
                <span class="sco-cand__name">{{ cand.contact_name || '—' }}</span>
                <span class="sco-cand__phone">{{ cand.phone }}</span>
              </div>
              <div v-if="cand.addresses && cand.addresses.length" class="sco-cand__row">
                <span class="sco-rk">Address</span>
                <span class="sco-rv"><span v-for="(a, i) in cand.addresses" :key="i" class="sco-cand__addr">{{ a }}</span></span>
              </div>
              <div v-if="cand.matched_tokens && cand.matched_tokens.length" class="sco-cand__row">
                <span class="sco-rk">Matched tokens</span>
                <span class="sco-rv"><span v-for="t in cand.matched_tokens" :key="t" class="sco-token sco-token--match">{{ t }}</span></span>
              </div>
              <div class="sco-cand__row">
                <span class="sco-rk">Care-of</span>
                <span class="sco-rv">
                  <span v-if="cand.care_of_user_name" class="sco-staff">{{ cand.care_of_user_name }}</span>
                  <span v-else class="sco-muted">No telesales call in this org — pick an agent below</span>
                </span>
              </div>
            </div>
          </label>
        </div>

        <div class="sco-review-head">
          <h4>Assign Agent Manually <span class="sco-optional">(optional override)</span></h4>
          <p>Override the care-of agent, or credit one when no call matched.</p>
        </div>
        <div class="sco-manual-assign">
          <AppDropdown v-model="manualAgentId" :options="manualAgentOptions" searchable search-placeholder="Search agent…" placeholder="Use matched agent" />
        </div>
      </div>
      <template #footer>
        <div class="sco-modal-foot">
          <div class="sco-modal-foot__left">
            <button type="button" class="sco-modal-btn sco-modal-btn--danger" :disabled="isApprovingReview" @click="dismissFromReview('rejected')">Reject</button>
            <button type="button" class="sco-modal-btn sco-modal-btn--warn" :disabled="isApprovingReview" @click="dismissFromReview('oms_customer')">OMS Customer</button>
          </div>
          <div class="sco-modal-foot__right">
            <button type="button" class="sco-modal-btn sco-modal-btn--ghost" :disabled="isApprovingReview" @click="closeReview">Cancel</button>
            <button type="button" class="sco-modal-btn sco-modal-btn--primary" :disabled="isApprovingReview || !canApproveReview" @click="approveReview">
              {{ isApprovingReview ? 'Applying…' : approveLabel }}
            </button>
          </div>
        </div>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="confirmApplyAllOpen"
      title="Apply All Confident Matches?"
      :message="applyAllMessage"
      confirm-label="Apply All"
      busy-label="Applying…"
      cancel-label="Cancel"
      :busy="isApplyingAll"
      variant="warning"
      @confirm="confirmApplyAll"
      @cancel="cancelApplyAll"
      @update:visible="(v) => !v && cancelApplyAll()"
    />

    <AppConfirmDialog
      :visible="rowActionOpen"
      :title="rowAction ? rowAction.title : ''"
      :message="rowAction ? rowAction.message : ''"
      :confirm-label="rowAction ? rowAction.confirmLabel : 'Confirm'"
      busy-label="Working…"
      cancel-label="Cancel"
      :busy="rowActionBusy"
      :variant="rowAction ? rowAction.variant : 'warning'"
      @confirm="confirmRowAction"
      @cancel="cancelRowAction"
      @update:visible="(v) => !v && cancelRowAction()"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppInput from '@/components/AppInput.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import { ORGANISATION_ID_TO_NAME } from '@/views/users/userHelpers.js'

function todayIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const DEFAULT_SUMMARY = { total: 0, confident: 0, needs_review: 0, no_match: 0, credited: 0, rejected: 0, oms_customer: 0 }

export default {
  name: 'StoreCareOf',
  components: { AppInput, AppDropdown, SingleDatePicker, BaseTable, AppPager, AppConfirmDialog },
  data () {
    return {
      filters: { from: '', to: todayIso(), organisation_id: '', status: '', search: '' },
      searchDebounce: null,
      agents: [],
      rows: [],
      summary: { ...DEFAULT_SUMMARY },
      pagination: { current_page: 1, per_page: 25, total: 0, last_page: 1 },
      isLoading: false,
      isRecomputing: false,
      isApplyingAll: false,
      applyingIds: {},
      confirmApplyAllOpen: false,
      errorMessage: '',
      successMessage: '',
      reviewOpen: false,
      reviewRow: null,
      reviewOrderTokens: [],
      reviewCandidates: [],
      reviewError: '',
      isLoadingCandidates: false,
      isApprovingReview: false,
      pickedContactId: null,
      pickedCareOfId: null,
      manualAgentId: '',
      rowActionOpen: false,
      rowAction: null,
      rowActionBusy: false
    }
  },
  computed: {
    columns () {
      return [
        { key: 'sno',          label: 'SNo',          width: '64px' },
        { key: 'order_number', label: 'Order #',      width: '130px', sticky: true },
        { key: 'customer',     label: 'Customer',     width: '240px', sticky: true },
        { key: 'phone',        label: 'Counter Phone', width: '120px' },
        { key: 'matched',      label: 'Matched Contact', width: '180px' },
        { key: 'staff',        label: 'Suggested Care-of', width: '150px' },
        { key: 'status',       label: 'Status' },
        { key: 'action',       label: 'Action',       width: '160px', align: 'center', stickyRight: true }
      ]
    },
    tableRows () {
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      return this.rows.map((row, index) => {
        const o = row.order || {}
        return {
          sno: (page - 1) * perPage + index + 1,
          order_number: o.order_number || ('#' + row.order_id),
          customer: o.contact_name || '—',
          address: this.composeAddress(o),
          phone: o.contact_phone || '—',
          _id: row.order_id,
          _status: row.status,
          _reason: row.reason,
          _real_phone: row.real_phone,
          _matched_contact_id: row.matched_contact_id,
          _matched_contact_name: row.matched_contact_name,
          _care_of_id: row.care_of_user_id,
          _care_of_name: row.care_of_user_name
        }
      })
    },
    orgOptions () {
      return [{ value: '', label: 'Any Organisation' }].concat(
        Object.keys(ORGANISATION_ID_TO_NAME).map((id) => ({ value: String(id), label: ORGANISATION_ID_TO_NAME[id] }))
      )
    },
    statusOptions () {
      const s = this.summary || {}
      const tag = (n) => (Number(n) > 0 ? ` (${n})` : '')
      return [
        { value: '',             label: 'Any Status' },
        { value: 'confident',    label: 'Confident' },
        { value: 'needs_review', label: 'Needs Review' },
        { value: 'no_match',     label: 'No Match' },
        { value: 'credited',     label: `Credited${tag(s.credited)}` },
        { value: 'rejected',     label: `Rejected${tag(s.rejected)}` },
        { value: 'oms_customer', label: `OMS Customer${tag(s.oms_customer)}` }
      ]
    },
    manualAgentOptions () {
      return [{ value: '', label: 'Use matched agent' }].concat(
        this.agents.map((u) => ({ value: String(u.id), label: u.name }))
      )
    },
    canApproveReview () {
      // Applying needs either a picked contact (fills phone + care-of) or a
      // manual agent (credits care-of only).
      return !!this.pickedContactId || !!this.manualAgentId
    },
    approveLabel () {
      return 'Credit Care-of'
    },
    applyAllMessage () {
      const count = Number(this.summary.confident) || 0
      return `${count} confident sale${count === 1 ? '' : 's'} will be credited to the telesales agent matched by name + address. The counter phone is left unchanged. Each order is re-checked before writing.`
    },
    rangeLabel () {
      const total = Number(this.pagination.total) || 0
      if (!total) return '0 orders'
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      const start = (page - 1) * perPage + 1
      const end = Math.min(page * perPage, total)
      return `${start}–${end} of ${total}`
    }
  },
  created () {
    this.loadAgents()
    this.refresh()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data) {
        if (typeof data === 'string') return data
        if (data.message) return data.message
        if (data.errors) {
          const k = Object.keys(data.errors)[0]
          if (k && Array.isArray(data.errors[k])) return data.errors[k][0]
        }
      }
      return fallback
    },
    composeAddress (o) {
      const parts = [o.contact_address, o.contact_postofficename, o.contact_district, o.contact_state, o.contact_pincode]
      return parts.filter((p) => p !== null && p !== undefined && String(p).trim() !== '').join(', ')
    },
    queryParams () {
      const p = { token: this.authToken() }
      if (this.filters.from) p.from = this.filters.from
      if (this.filters.to) p.to = this.filters.to
      if (this.filters.organisation_id) p.organisation_id = this.filters.organisation_id
      if (this.filters.status) p.status = this.filters.status
      if (this.filters.search && this.filters.search.trim()) p.search = this.filters.search.trim()
      return p
    },
    loadAgents () {
      axios.get(`${this.$apiAdress}/api/users?token=${this.authToken()}`)
        .then((response) => {
          const list = Array.isArray(response.data)
            ? response.data
            : (response && response.data && Array.isArray(response.data.users) ? response.data.users : [])
          this.agents = list.map((u) => ({ id: u.id, name: u.name || `User ${u.id}` }))
        })
        .catch(() => {})
    },
    refresh () {
      this.errorMessage = ''
      this.successMessage = ''
      this.loadSummary()
      this.loadPreview(1)
    },
    loadSummary () {
      axios.get(`${this.$apiAdress}/api/store-care-of/summary`, { params: this.queryParams() })
        .then((response) => {
          this.summary = { ...DEFAULT_SUMMARY, ...(response && response.data ? response.data : {}) }
        })
        .catch((error) => {
          console.log(error)
          this.summary = { ...DEFAULT_SUMMARY }
        })
    },
    loadPreview (page) {
      this.isLoading = true
      const targetPage = Number(page) || Number(this.pagination.current_page) || 1
      const params = Object.assign({}, this.queryParams(), { page: targetPage, per_page: this.pagination.per_page || 25 })

      axios.get(`${this.$apiAdress}/api/store-care-of/preview`, { params })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.rows = Array.isArray(data.data) ? data.data : []
          this.pagination = {
            current_page: Number(data.current_page) || 1,
            per_page: Number(data.per_page) || 25,
            total: Number(data.total) || 0,
            last_page: Number(data.last_page) || 1
          }
        })
        .catch((error) => {
          console.log(error)
          this.rows = []
          this.errorMessage = this.parseError(error, 'Unable to load the Store order preview.')
        })
        .finally(() => { this.isLoading = false })
    },
    applyFilters () {
      this.refresh()
    },
    onSearchInput (value) {
      this.filters.search = value
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => { this.refresh() }, 400)
    },
    resetFilters () {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.filters = { from: '', to: todayIso(), organisation_id: '', status: '', search: '' }
      this.refresh()
    },
    goToPage (page) {
      if (Number(page) === Number(this.pagination.current_page)) return
      this.loadPreview(Number(page))
    },
    applyConfident (row) {
      const id = row._id
      if (!id || this.applyingIds[id]) return
      this.$set(this.applyingIds, id, true)
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/store-care-of/${id}/apply?token=${this.authToken()}`, {
        contact_id: row._matched_contact_id,
        care_of_user_id: row._care_of_id
      })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Applied.'
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Apply failed.')
        })
        .finally(() => { this.$set(this.applyingIds, id, false) })
    },
    openReview (row) {
      this.reviewRow = row
      this.reviewOrderTokens = []
      this.reviewCandidates = []
      this.reviewError = ''
      this.pickedContactId = null
      this.pickedCareOfId = null
      this.manualAgentId = ''
      this.reviewOpen = true
      this.loadCandidates(row._id)
    },
    closeReview () {
      if (this.isApprovingReview) return
      this.reviewOpen = false
      this.reviewRow = null
      this.reviewCandidates = []
      this.reviewOrderTokens = []
      this.reviewError = ''
      this.pickedContactId = null
      this.pickedCareOfId = null
      this.manualAgentId = ''
    },
    loadCandidates (id) {
      if (!id) return
      this.isLoadingCandidates = true
      axios.get(`${this.$apiAdress}/api/store-care-of/${id}/candidates`, { params: { token: this.authToken() } })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.reviewOrderTokens = Array.isArray(data.order_tokens) ? data.order_tokens : []
          this.reviewCandidates = Array.isArray(data.candidates) ? data.candidates : []
          // Pre-select when there's exactly one candidate — the admin just verifies.
          if (this.reviewCandidates.length === 1) {
            this.pickCandidate(this.reviewCandidates[0])
          }
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Unable to load candidates.')
        })
        .finally(() => { this.isLoadingCandidates = false })
    },
    pickCandidate (cand) {
      this.pickedContactId = cand.contact_id
      this.pickedCareOfId = cand.care_of_user_id || null
    },
    approveReview () {
      if (!this.reviewRow || !this.canApproveReview) return
      this.isApprovingReview = true
      this.reviewError = ''

      const payload = {}
      if (this.pickedContactId) {
        payload.contact_id = this.pickedContactId
      }
      // Manual override wins; otherwise the picked candidate's resolved agent.
      const careOf = this.manualAgentId ? Number(this.manualAgentId) : this.pickedCareOfId
      if (careOf) payload.care_of_user_id = careOf

      axios.post(`${this.$apiAdress}/api/store-care-of/${this.reviewRow._id}/apply?token=${this.authToken()}`, payload)
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Applied.'
          this.closeReviewAfterSuccess()
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Apply failed.')
        })
        .finally(() => { this.isApprovingReview = false })
    },
    closeReviewAfterSuccess () {
      this.reviewOpen = false
      this.reviewRow = null
      this.reviewCandidates = []
      this.pickedContactId = null
      this.pickedCareOfId = null
      this.manualAgentId = ''
    },
    // Dismiss a pending order from inside the review modal without crediting.
    dismissFromReview (status) {
      if (!this.reviewRow || this.isApprovingReview) return
      this.isApprovingReview = true
      this.reviewError = ''

      axios.post(`${this.$apiAdress}/api/store-care-of/${this.reviewRow._id}/status?token=${this.authToken()}`, { status })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Updated.'
          this.closeReviewAfterSuccess()
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Update failed.')
        })
        .finally(() => { this.isApprovingReview = false })
    },
    askUncredit (row) {
      this.rowAction = {
        kind: 'uncredit',
        id: row._id,
        title: 'Reverse this credit?',
        message: 'The care-of credit will be removed and the sale returned to the pending list so it can be re-credited or dismissed. The counter phone is unaffected.',
        confirmLabel: 'Uncredit',
        variant: 'danger'
      }
      this.rowActionOpen = true
    },
    askReopen (row) {
      const what = row._status === 'rejected' ? 'rejected' : 'OMS-customer'
      this.rowAction = {
        kind: 'reopen',
        id: row._id,
        title: 'Re-open to pending?',
        message: `This ${what} order will return to the pending list to be credited or dismissed again.`,
        confirmLabel: 'Re-open',
        variant: 'warning'
      }
      this.rowActionOpen = true
    },
    confirmRowAction () {
      const a = this.rowAction
      if (!a || this.rowActionBusy) return
      this.rowActionBusy = true
      this.errorMessage = ''
      this.successMessage = ''

      const url = a.kind === 'uncredit'
        ? `${this.$apiAdress}/api/store-care-of/${a.id}/uncredit?token=${this.authToken()}`
        : `${this.$apiAdress}/api/store-care-of/${a.id}/status?token=${this.authToken()}`
      const body = a.kind === 'uncredit' ? {} : { status: 'pending' }

      axios.post(url, body)
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Done.'
          this.rowActionOpen = false
          this.rowAction = null
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Action failed.')
          this.rowActionOpen = false
        })
        .finally(() => { this.rowActionBusy = false })
    },
    cancelRowAction () {
      if (this.rowActionBusy) return
      this.rowActionOpen = false
      this.rowAction = null
    },
    askApplyAll () {
      if (this.isApplyingAll || !this.summary.confident) return
      this.confirmApplyAllOpen = true
    },
    confirmApplyAll () {
      this.isApplyingAll = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/store-care-of/apply-all?token=${this.authToken()}`, this.queryParams())
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Applied.'
          this.confirmApplyAllOpen = false
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Bulk apply failed.')
          this.confirmApplyAllOpen = false
        })
        .finally(() => { this.isApplyingAll = false })
    },
    cancelApplyAll () {
      if (this.isApplyingAll) return
      this.confirmApplyAllOpen = false
    },
    recompute () {
      this.isRecomputing = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/store-care-of/recompute?token=${this.authToken()}`, this.queryParams())
        .then((response) => {
          this.summary = { ...DEFAULT_SUMMARY, ...(response && response.data ? response.data : {}) }
          this.successMessage = 'Recompute complete.'
          this.loadPreview(1)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Recompute failed.')
        })
        .finally(() => { this.isRecomputing = false })
    },
    statusLabel (status) {
      switch (status) {
        case 'confident':    return 'Confident'
        case 'needs_review': return 'Needs Review'
        case 'no_match':     return 'No Match'
        case 'credited':     return 'Credited'
        case 'rejected':     return 'Rejected'
        case 'oms_customer': return 'OMS Customer'
        default:             return status || '—'
      }
    },
    statusPillClass (status) {
      switch (status) {
        case 'confident':    return 'sco-pill--green'
        case 'needs_review': return 'sco-pill--amber'
        case 'no_match':     return 'sco-pill--gray'
        case 'credited':     return 'sco-pill--blue'
        case 'rejected':     return 'sco-pill--red'
        case 'oms_customer': return 'sco-pill--violet'
        default:             return 'sco-pill--gray'
      }
    }
  }
}
</script>

<style scoped>
.sco-page { padding: 0.25rem 0; }

.sco-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; }
.sco-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.sco-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.sco-toolbar__title p  { margin: 0.1rem 0 0; font-size: 0.78rem; color: #6b7280; }
.sco-toolbar__accent { width: 4px; height: 32px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }
.sco-toolbar__actions { display: inline-flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

.sco-recompute-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
  font-weight: 600; font-size: 0.82rem; cursor: pointer;
}
.sco-recompute-btn:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.sco-recompute-btn:disabled { opacity: 0.7; cursor: progress; }
.sco-recompute-btn svg.is-spinning { animation: sco-spin 0.9s linear infinite; }
.sco-recompute-btn:focus { outline: none; }
.sco-recompute-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.sco-apply-all-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #fff; font-weight: 600; font-size: 0.82rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); cursor: pointer;
}
.sco-apply-all-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.sco-apply-all-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.sco-apply-all-btn:focus { outline: none; }
.sco-apply-all-btn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
@keyframes sco-spin { to { transform: rotate(360deg); } }

.sco-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.sco-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.85rem; margin-bottom: 1rem; }
.sco-tile { background: #ffffff; border: 1px solid #e5e9f2; border-radius: 12px; padding: 0.9rem 1rem; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
.sco-tile__label { font-size: 0.74rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.sco-tile__value { font-size: 1.5rem; font-weight: 700; color: #1f2a44; }
.sco-tile__breakdown { display: flex; flex-direction: column; gap: 0.1rem; font-size: 0.72rem; color: #64748b; }
.sco-tile--confident .sco-tile__value { color: #166534; }
.sco-tile--review .sco-tile__value { color: #92400e; }
.sco-tile--nomatch .sco-tile__value { color: #6b7280; }

.sco-section { background: #ffffff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); margin-bottom: 1rem; }
.sco-section__head { padding: 0.7rem 1rem; border-bottom: 1px solid #eef1f7; background: #fbfcfe; border-radius: 12px 12px 0 0; }
.sco-section__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; }
.sco-section__body { padding: 1rem; }

.sco-label { display: block; margin-bottom: 0.3rem; font-size: 0.78rem; font-weight: 600; color: #4f5560; }
.sco-filters { display: flex; flex-wrap: wrap; gap: 0.85rem; align-items: flex-end; }
.sco-field { display: flex; flex-direction: column; flex: 1 1 200px; min-width: 0; }
.sco-field--action { flex: 0 0 auto; min-width: 110px; }
.sco-field--full { flex: 1 1 100%; }

.sco-apply-btn {
  height: 36px; padding: 0 1.1rem; border-radius: 8px; border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-weight: 600; font-size: 0.85rem; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); cursor: pointer;
}
.sco-apply-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.sco-apply-btn:disabled { opacity: 0.55; cursor: progress; }
.sco-apply-btn:focus { outline: none; }
.sco-apply-btn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }

.sco-reset-btn { height: 36px; padding: 0 1rem; border-radius: 8px; background: #ffffff; border: 1px solid #d8dde6; color: #1f2a44; font-weight: 600; font-size: 0.85rem; cursor: pointer; }
.sco-reset-btn:hover { border-color: #94a3b8; }
.sco-reset-btn:focus { outline: none; }
.sco-reset-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.sco-count { font-size: 0.78rem; color: #6b7280; }

.sco-customer-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.sco-customer-name { color: #1f2a44; font-weight: 500; }
.sco-customer-addr { font-size: 0.72rem; color: #64748b; max-width: 230px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.sco-match-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.sco-match-phone { color: #0e5b46; font-weight: 600; }
.sco-match-name { font-size: 0.72rem; color: #64748b; }
.sco-staff { color: #1f2a44; font-weight: 500; }
.sco-muted { color: #94a3b8; }

.sco-reason { font-size: 0.72rem; color: #64748b; margin-top: 0.2rem; max-width: 360px; }

.sco-pill { display: inline-block; padding: 0.18rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; }
.sco-pill--green  { background: #dcfce7; color: #166534; }
.sco-pill--amber  { background: #fef3c7; color: #92400e; }
.sco-pill--gray   { background: #e5e7eb; color: #374151; }
.sco-pill--blue   { background: #dbeafe; color: #1e40af; }
.sco-pill--red    { background: #fee2e2; color: #991b1b; }
.sco-pill--violet { background: #ede9fe; color: #5b21b6; }

.sco-row-actions { display: inline-flex; gap: 0.35rem; flex-wrap: wrap; align-items: center; justify-content: center; }

.sco-act-btn {
  height: 28px; padding: 0 0.7rem; border: 0; border-radius: 6px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-size: 0.75rem; font-weight: 700; cursor: pointer; letter-spacing: 0.02em;
  box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.sco-act-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 4px 10px rgba(14, 91, 70, 0.28); }
.sco-act-btn:disabled { opacity: 0.55; cursor: progress; }
.sco-act-btn:focus { outline: none; }
.sco-act-btn:focus-visible { outline: none; box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
.sco-act-btn--review { background: linear-gradient(135deg, #b45309 0%, #d97706 100%); box-shadow: 0 1px 3px rgba(180, 83, 9, 0.22); }
.sco-act-btn--review:hover:not(:disabled) { box-shadow: 0 4px 10px rgba(180, 83, 9, 0.32); }
.sco-act-btn--danger { background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%); box-shadow: 0 1px 3px rgba(185, 28, 28, 0.22); }
.sco-act-btn--danger:hover:not(:disabled) { box-shadow: 0 4px 10px rgba(185, 28, 28, 0.32); }
.sco-act-btn--danger:focus-visible { outline: none; box-shadow: 0 1px 3px rgba(185, 28, 28, 0.22), 0 0 0 3px rgba(220, 38, 38, 0.30); }
.sco-act-btn--ghost { background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44; box-shadow: none; }
.sco-act-btn--ghost:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; box-shadow: none; transform: none; }
.sco-act-btn--ghost:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.sco-pagination-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.85rem; }
.sco-pagination-info { font-size: 0.78rem; color: #6b7280; }

/* Review modal */
.sco-review-body { display: flex; flex-direction: column; gap: 0.9rem; }
.sco-review-alert { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; border-radius: 8px; padding: 0.5rem 0.7rem; font-size: 0.82rem; }
.sco-review-order { background: #fbfcfe; border: 1px solid #eef1f7; border-radius: 10px; padding: 0.85rem; }
.sco-review-order__head { display: flex; flex-direction: column; gap: 0.1rem; margin-bottom: 0.5rem; }
.sco-review-eyebrow { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #d97706; }
.sco-review-order__head h3 { margin: 0; font-size: 1rem; font-weight: 700; color: #1f2a44; }
.sco-review-order__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.4rem 1rem; }
.sco-review-order__addr { grid-column: 1 / -1; }
.sco-rk { display: block; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: #94a3b8; }
.sco-rv { font-size: 0.85rem; color: #1f2a44; }

.sco-review-head { margin-top: 0.2rem; }
.sco-review-head h4 { margin: 0; font-size: 0.9rem; font-weight: 700; color: #1f2a44; }
.sco-review-head p { margin: 0.2rem 0 0; font-size: 0.76rem; color: #6b7280; }
.sco-optional { font-weight: 500; color: #94a3b8; font-size: 0.78rem; }
.sco-review-empty { font-size: 0.82rem; color: #64748b; padding: 0.5rem 0; }

.sco-review-cards { display: flex; flex-direction: column; gap: 0.5rem; max-height: 320px; overflow-y: auto; }
.sco-cand { display: flex; gap: 0.6rem; padding: 0.65rem 0.75rem; border: 1px solid #e5e9f2; border-radius: 10px; cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease; }
.sco-cand:hover { border-color: #16a07a; background: #f5fbf8; }
.sco-cand.is-picked { border-color: #16a07a; background: #f0fbf6; box-shadow: 0 0 0 1px #16a07a inset; }
.sco-cand__radio { margin-top: 0.2rem; accent-color: #0e5b46; }
.sco-cand__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.3rem; }
.sco-cand__head { display: flex; justify-content: space-between; gap: 0.5rem; }
.sco-cand__name { font-weight: 700; color: #1f2a44; }
.sco-cand__phone { color: #0e5b46; font-weight: 600; font-size: 0.85rem; }
.sco-cand__row { display: flex; flex-direction: column; gap: 0.1rem; }
.sco-cand__addr { display: block; font-size: 0.8rem; color: #475569; }

.sco-token { display: inline-block; padding: 0.08rem 0.4rem; margin: 0.1rem 0.2rem 0 0; border-radius: 999px; font-size: 0.7rem; background: #eef2f7; color: #475569; }
.sco-token--match { background: #dcfce7; color: #166534; font-weight: 600; }

.sco-manual-assign { display: flex; flex-direction: column; gap: 0.5rem; }

.sco-modal-btn { height: 34px; min-width: 90px; padding: 0.45rem 1.1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; }
.sco-modal-btn--ghost { background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44; }
.sco-modal-btn--ghost:hover { background: #f8fafc; }
.sco-modal-btn--primary { border: 0; color: #fff; background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); }
.sco-modal-btn--primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.sco-modal-btn--danger { border: 0; color: #fff; background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%); box-shadow: 0 2px 6px rgba(185, 28, 28, 0.18); }
.sco-modal-btn--danger:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(185, 28, 28, 0.28); filter: brightness(1.05); }
.sco-modal-btn--warn { border: 0; color: #fff; background: linear-gradient(135deg, #b45309 0%, #d97706 100%); box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18); }
.sco-modal-btn--warn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(180, 83, 9, 0.28); filter: brightness(1.05); }
.sco-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.sco-modal-btn:focus { outline: none; }
.sco-modal-btn--ghost:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.sco-modal-btn--primary:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
.sco-modal-btn--danger:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(185, 28, 28, 0.18), 0 0 0 3px rgba(220, 38, 38, 0.30); }
.sco-modal-btn--warn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18), 0 0 0 3px rgba(217, 119, 6, 0.30); }

.sco-modal-foot { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; width: 100%; flex-wrap: wrap; }
.sco-modal-foot__left, .sco-modal-foot__right { display: inline-flex; gap: 0.5rem; align-items: center; }
</style>
